import 'moment-timezone'

import { Close, Link } from '@carbon/icons-react'
import { DDSInput, DDSTextArea, DDSTypography } from 'den-design-system'
import React, { useEffect } from 'react'
import { createSearchParams, useNavigate, useParams } from 'react-router-dom'

import { AccessRoleConstants } from '../../constants/AccessRoleConstants'
import { Button } from '../../components/New/Button/Button'
import DatePicker from '../../components/New/Datepicker/Datepicker'
import DocumentHeader from '../../components/Helmet/DocumentHeader'
import { HeaderConstants } from '../../constants/HeaderConstants'
import IncidentConstants from '../../constants/IncidentConstants'
import MultiMedia from './MediaUpload'
import RegexConstants from '../../constants/RegexConstants'
import SecureComponent from '../../components/Layout/SecureComponent'
import Select from '../../components/New/Select/Select'
import Spinner from '../../components/common/Spinner/Spinner'
import { ToastMessageConstants } from '../../constants/ToastMessageConstants'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import _ from 'lodash'
import { convertToUTC } from '../../utils/DateTimeUtils'
import { createIncident } from '../../api/incident/incident.service'
import dayjs from 'dayjs'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { useState } from 'react'
import { useStoreContext } from '../../store/StoreContext'

interface MediaFile {
  id: string
  file: File
  fileUrl: string
}

const ReportIncidentPage = () => {
  const [shortDescription, setShortDescription] = useState<string>('')
  const [longDescription, setLongDescription] = useState<string>('')
  const [reference, setReference] = useState<string>('')
  const [referenceLinks, setReferenceLinks] = useState<string[]>([])
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([])
  const [priorityStatus, setPriorityStatus] = useState<string>('P0')

  const [identifiedOn, setIdentifiedOn] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [isFormValid, setIsFormValid] = useState(false)
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)
  const [createButtonDisabled, setCreateButtonDisabled] = useState(false)
  const { exceptionLogId } = useParams()
  const navigate = useNavigate()

  const store = useStoreContext()

  const projects = [
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed1 }}>P0</span> -
          Critical
        </div>
      ),
      value: 'P0'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed2 }}>P1</span> - High
        </div>
      ),
      value: 'P1'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningRed3 }}>P2</span> -
          Moderate
        </div>
      ),
      value: 'P2'
    },
    {
      label: (
        <div>
          <span style={{ color: pallete.colors.warningBlue }}>P3</span> - Low
        </div>
      ),
      value: 'P3'
    }
  ]

  useEffect(() => {
    if (
      priorityStatus !== '' &&
      identifiedOn &&
      shortDescription !== '' &&
      longDescription !== ''
    ) {
      setIsFormValid(true)
      setCreateButtonDisabled(false)
    } else {
      setIsFormValid(false)
      setCreateButtonDisabled(true)
    }
  }, [priorityStatus, identifiedOn, shortDescription, longDescription])

  const handleCreateAnIncident = () => {
    setLoading(true)
    store.uiStore.setGlobalLoader(true)

    const formattedDate = moment(new Date(identifiedOn)).format('l')
    const timeZone = store.userStore.getOrganizationPreference().timeZone
    const timezoneOffset = moment.tz(timeZone).format('Z')
    const formattedDateWithTimeZone = `${formattedDate} ${timezoneOffset}`
    const formData = new FormData()
    if (exceptionLogId) {
      formData.append('exceptionLogId', String(exceptionLogId))
    }

    formData.append('priority', priorityStatus)
    formData.append('identifiedOn', convertToUTC(formattedDateWithTimeZone))
    formData.append('briefDescription', shortDescription)
    formData.append('longDescription', longDescription),
      formData.append('savedLinks', JSON.stringify(referenceLinks))

    mediaFiles?.forEach((file, index) => {
      formData.append(`files`, file.file)
    })

    createIncident(formData)
      .then((response: any) => {
        const incidentId = response.data.id

        ToastNotification({
          type: 'success',
          message: ToastMessageConstants.INCIDENT.CREATE_INCIDENT.SUCCESS
        })

        const route = replaceUrl(
          routeConstants.MANAGE_INCIDENTS,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        ).replace(':incidentId', String(incidentId))

        const queryParams = { tab: '1' }
        navigate({
          pathname: route,
          search: `?${createSearchParams(queryParams)}`
        })
      })
      .catch((error: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.INCIDENT.CREATE_INCIDENT.ERROR
        })
      })
      .finally(() => {
        setLoading(false)
        store.uiStore.setGlobalLoader(false)
      })
  }

  const validateForm = () => {
    const createIncidentDTO = {
      priority: priorityStatus,
      identifiedOn: identifiedOn,
      briefDescription: shortDescription,
      longDescription: longDescription
    }

    const emptyDTO = _.some(_.values(createIncidentDTO), _.isEmpty)
    setIsFormValid(!emptyDTO)
  }

  const handleCancel = () => {
    const route = replaceUrl(
      routeConstants.INCIDENT,
      store.scopeStore.getScope()['organization-id'] as string,
      store.scopeStore.getScope()['project-id'] as string,
      store.scopeStore.getScope()['environment-id'] as string
    )

    const queryParams = { page: '1', tab: '1' }
    navigate({
      pathname: route,
      search: `?${createSearchParams(queryParams)}`
    })

    setShortDescription('')
    setLongDescription('')
    setReference('')
    setReferenceLinks([])
    setPriorityStatus('P0')
    setIdentifiedOn(null)
  }

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Incident',
        link:
          replaceUrl(
            routeConstants.INCIDENT,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?page=1&tab=1'
      },
      {
        label: 'Report an Incident',
        link: replaceUrl(
          routeConstants.REPORT_INCIDENT,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])
  useEffect(() => {
    validateForm()
  }, [
    priorityStatus,
    identifiedOn,
    shortDescription,
    longDescription,
    referenceLinks
  ])

  const getMaxDate = () => {
    const orgTimezone = store.userStore.getOrganizationPreference().timeZone

    const timezoneString = moment()
      .tz(orgTimezone)
      .format('YYYY-MM-DDTHH:mm:ss')

    const maxDate = new Date(timezoneString)
    return dayjs(maxDate)
  }

  return (
    <>
      <DocumentHeader header={HeaderConstants.INCIDENT} />
      {store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='gap-[4px] flex flex-col justify-between h-full'>
          <div className='py-[24px]'>
            <DDSTypography.Title
              variant='semiBold'
              type='h3'
              color={pallete.colors.textDark3}
              className='gap-[32px]'
            >
              {IncidentConstants.REPORT_AN_INCIDENT}
            </DDSTypography.Title>
            <div className='pt-[32px] gap-x-[52px] flex flex-row'>
              <div className='w-[65%]'>
                <div className='flex flex-row gap-[24px] w-[100%] incident-select '>
                  <div className='flex items-center'>
                    <Select
                      size='large'
                      id='projects'
                      variant='top'
                      name='projects'
                      label={'Priority'}
                      options={projects}
                      value={priorityStatus}
                      defaultValue={priorityStatus}
                      searchable={false}
                      required
                      disabled={store.uiStore.getGlobalLoader()}
                      placeholder='Choose'
                      onSelect={(value) => {
                        setPriorityStatus(value)
                      }}
                    />
                  </div>
                  <div className='override-dds-input-field-incident-page  flex items-center'>
                    <DatePicker
                      id='report-incident-date-picker'
                      name='Identified on'
                      inputWidth={`${
                        store.uiStore.getSidebarExpanded() ? '386px' : '500px'
                      }`}
                      inputHeight='32px'
                      format='MM/dd/YYYY'
                      maxDate={getMaxDate()}
                      required
                      value={identifiedOn}
                      label='Identified on'
                      shape='roundedRectangle'
                      placeholder='Select Date'
                      onChange={(date: any) => {
                        setIdentifiedOn(date)
                      }}
                    />
                  </div>
                </div>
                <div className='pt-[32px]'>
                  <DDSInput
                    name='Title'
                    label='Title'
                    height='32px'
                    required
                    shape='roundedRectangle'
                    type='text'
                    maxLength={150}
                    onChange={(e) => {
                      setShortDescription(e.target.value)
                    }}
                    style={{
                      borderRadius: 4,
                      width: '100%'
                    }}
                    value={shortDescription}
                    validateInput={true}
                    variant='plain'
                  />
                </div>
                <div className='pt-[32px] override-textarea-border-radius override-text-area '>
                  <DDSTextArea
                    name='Description'
                    label='Description'
                    className='override-text-area'
                    required={true}
                    minRow={5}
                    maxLength={2000}
                    style={{
                      borderRadius: '6px',
                      width: '100%'
                    }}
                    value={longDescription}
                    onChange={(e) => {
                      setLongDescription(e.target.value)
                    }}
                  />
                </div>
                <div className='!w-[100%] flex pt-[32px]  flex-row align-center gap-[24px]'>
                  <div className='!w-[100%] reference-link '>
                    <DDSInput
                      label='Reference Links'
                      id='input-incident'
                      shape='roundedRectangle'
                      variant='plain'
                      prefixIcon={
                        <Link
                          size='16px'
                          color={pallete.colors.textDark7}
                        ></Link>
                      }
                      placeholder='Paste your link here'
                      width='100%'
                      height='32px'
                      validationRegex={
                        new RegExp(RegexConstants.REFERENCE_REGEX)
                      }
                      validate={(value: string) => {
                        if (
                          RegexConstants.REFERENCE_REGEX.test(value) &&
                          referenceLinks.includes(value)
                        ) {
                          return {
                            error: true,
                            message: 'Reference link already exists'
                          }
                        } else if (value === '') {
                          return {
                            error: false,
                            message: ''
                          }
                        } else if (
                          !RegexConstants.REFERENCE_REGEX.test(value)
                        ) {
                          return {
                            error: true,
                            message: 'Please enter a valid reference link'
                          }
                        }
                        return {
                          error: false,
                          message: ''
                        }
                      }}
                      type='text'
                      onChange={(e) => {
                        setReference(e.target.value)
                        if (
                          referenceLinks.includes(e.target.value) ||
                          !RegexConstants.REFERENCE_REGEX.test(e.target.value)
                        ) {
                          setAddButtonDisabled(true)
                        } else {
                          setAddButtonDisabled(false)
                        }
                      }}
                      value={reference}
                      name='referenceLinks'
                      validateInput={true}
                      onError={(error: any) => {
                        setAddButtonDisabled(error)
                      }}
                      className='cursor-pointer'
                    />
                  </div>
                  <div className='incident-add-button my-[28px]'>
                    <Button
                      id='add-links'
                      disabled={
                        !reference ||
                        referenceLinks.includes(reference) ||
                        addButtonDisabled
                      }
                      size='medium'
                      label='Add Link'
                      onClick={() => {
                        if (reference && !referenceLinks.includes(reference)) {
                          setReferenceLinks([reference, ...referenceLinks])
                          setReference('')
                          setAddButtonDisabled(false)
                        } else {
                          setAddButtonDisabled(true)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='flex flex-row flex-wrap w-full gap-[8px]'>
                  {referenceLinks.map((item, index) => (
                    <div
                      className='flex w-[120px] h-[36px] items-center justify-between p-[6px] bg-[#F5F7F9] rounded-md'
                      key={index}
                    >
                      <div
                        className='flex gap-[10px] items-center cursor-pointer'
                        onClick={(e: any) => {
                          e.preventDefault()
                          window.open(item, '_blank', 'noopener,noreferrer')
                        }}
                      >
                        <div>
                          <Link
                            size={25}
                            className='p-[4px] text-[#086cff] rounded-full'
                          />
                        </div>
                        <div>
                          <DDSTypography.Paragraph
                            variant='medium'
                            size='para'
                            link={false}
                            href={item}
                            color={pallete.colors.black}
                            className={`flex text-sm text-ellipsis truncate ${
                              store.uiStore.getSidebarExpanded()
                                ? 'max-w-[670px] 2xl:max-w-[950px]'
                                : 'max-w-[790px] 2xl:max-w-[1060px]'
                            }`}
                          >
                            {`Link ${index + 1}`}
                          </DDSTypography.Paragraph>
                        </div>
                      </div>
                      <div className='flex w-[20px]'>
                        <Close
                          size={20}
                          color={pallete.colors.textButtonText}
                          onClick={() => {
                            const updatedLinks = referenceLinks.filter(
                              (_, i) => i !== index
                            )
                            setReferenceLinks(updatedLinks)

                            // Optional: reset add button if reference matches any existing link
                            setAddButtonDisabled(
                              updatedLinks.includes(reference)
                            )
                          }}
                          className='cursor-pointer'
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className='pt-[8px]'>
                  <MultiMedia
                    type='create'
                    mediaFiles={mediaFiles}
                    setMediaFiles={setMediaFiles}
                    disabled={store.uiStore.getGlobalLoader()}
                  />
                </div>
              </div>
              <div className='w-[35%]'>
                <div
                  style={{
                    backgroundColor: pallete.colors.surface6,
                    padding: 24,
                    borderRadius: 10
                  }}
                >
                  <DDSTypography.Title
                    variant='bold'
                    type='h5'
                    style={{ marginBottom: 12 }}
                    color={pallete.colors.textDark4}
                  >
                    <span>{IncidentConstants.WHATS_GOING_ON}</span>
                  </DDSTypography.Title>
                  <DDSTypography.Paragraph
                    variant='medium'
                    size='para'
                    color={pallete.colors.textDark6}
                  >
                    {IncidentConstants.WHATS_GOING_ON_DESC}
                  </DDSTypography.Paragraph>
                </div>
              </div>
            </div>
          </div>
          <div className='flex items-end justify-end relative'>
            <hr className='w-full h-[1px] flex absolute top-0' />
            <div className='flex items-end justify-end px-[20px] py-[20px] gap-x-[12px]'>
              <Button
                id='cancel-incident'
                type='neutral'
                size='medium'
                label='Cancel'
                onClick={handleCancel}
              />
              <Button
                id='create-incident'
                size='medium'
                label='Create Incident'
                onClick={handleCreateAnIncident}
                disabled={createButtonDisabled}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SecureComponent(
  observer(ReportIncidentPage),
  AccessRoleConstants.MEMBER_LEVEL
)
